/**
 * Club settings — /a/settings (owner's direction, 2 Sep 2026).
 *
 * The committee's money desk, in one place with its own rail tab:
 *
 *   1. MEMBERSHIP FEES — what the club charges, per tier, with the joining
 *      amount and the renewal amount as separate numbers. Edited in a popup
 *      (FeesSheet); every link, sheet and total in the app reads from here.
 *   2. HOW MEMBERS PAY — the collection setup every public payment link
 *      shows: QR / bank transfer (holder, account number, IFSC, QR image,
 *      member attaches a payment screenshot) or Razorpay with the club's own
 *      key pair for instant payment.
 *   3. HOME GROUNDS — the club's own list of places it runs at. Every form
 *      that asks for a home ground offers exactly these and nothing else
 *      (owner's direction, 2 Sep 2026); the member form used to carry an
 *      inline "+ New ground…", which put a club-wide, permanent decision
 *      inside a form about one person.
 *
 * The first two lived at the bottom of the Dues screen before they had a
 * home; Dues now links here instead of hosting them.
 */

import { useState } from 'react';
import { clubFees, TIERS } from '../../data';
import { useClub, useClubData, useGrounds } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { ConfirmSheet } from '../../components/sheets/ClubSheets';
import { FeesSheet, PaymentDetailsSheet } from '../../components/sheets/MoneySheets';
import { Button, Empty, Field, FormError, Hint, Pill, ViewTitle } from '../../components/ui';

const SEC_HEAD = 'mb-2.5 flex flex-wrap items-center justify-between gap-2';
const SEC_TITLE = 'font-market text-[11px] font-bold uppercase tracking-[0.2em] text-copy-label';

export function SettingsPage() {
  const { db } = useClubData();
  const { openSheet } = useUI();
  const meta = db.meta;
  const fees = clubFees(meta.fees);

  return (
    <>
      <ViewTitle title="Club settings" sub="What you charge, and how members pay you" />

      {/* ------------------------------------------------ membership fees -- */}
      <div className="mb-8">
        <div className={SEC_HEAD}>
          <h2 className={SEC_TITLE}>Membership fees</h2>
          <Button small tone="p" onClick={() => openSheet(<FeesSheet />)}>
            Change amounts
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-px border border-line bg-line min-[560px]:grid-cols-4">
          {Object.keys(TIERS).map((tier) => (
            <div key={tier} className="bg-white px-3.5 py-3">
              <div className="font-market text-[9.5px] uppercase tracking-[0.12em] text-copy-muted">
                {TIERS[tier].label}
              </div>
              <div className="mt-1 font-market text-[15px] font-extrabold tabular-nums text-ink-700">
                ₹{fees[tier].join.toLocaleString('en-IN')}
                <span className="font-normal text-copy-label"> join</span>
              </div>
              <div className="font-market text-[12px] tabular-nums text-copy-muted">
                {TIERS[tier].months === null
                  ? 'one-time'
                  : `₹${fees[tier].renew.toLocaleString('en-IN')} renew`}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[12px] leading-relaxed text-copy-muted">
          A member who has <b className="font-semibold text-ink-700">never paid</b> is
          charged the joining amount; everyone else the renewal. Every payment
          link, sheet and total in the app uses these figures.
        </p>
      </div>

      {/* ---------------------------------------------- collection setup -- */}
      <div className={SEC_HEAD}>
        <h2 className={SEC_TITLE}>How members pay you</h2>
        <Button
          small
          tone={meta.payMode === 'razorpay' || meta.bankUpi || meta.bankAccount || meta.bankQrUrl ? 'g' : 'p'}
          onClick={() => openSheet(<PaymentDetailsSheet meta={meta} />)}
        >
          {meta.bankUpi || meta.bankAccount || meta.bankQrUrl || meta.rpConfigured
            ? 'Change'
            : 'Set up'}
        </Button>
      </div>

      {meta.payMode === 'razorpay' ? (
        <div className="border border-line bg-white p-4">
          <div className="flex flex-wrap items-center gap-2.5">
            <Pill tone={meta.rpConfigured ? 'ok' : 'late'}>
              {meta.rpConfigured ? 'Razorpay · instant' : 'Razorpay · keys missing'}
            </Pill>
            {meta.rpKeyId && (
              <span className="font-market text-[12px] tabular-nums text-copy-muted">
                Key {meta.rpKeyId}
              </span>
            )}
          </div>
          <p className="mt-2 text-[12.5px] leading-relaxed text-copy-muted">
            Members on a payment link pay instantly by UPI, card or netbanking
            through the club&rsquo;s own Razorpay account. The key secret is stored
            server-side and never shown.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-4 border border-line bg-white p-4">
          {meta.bankQrUrl ? (
            <img
              src={meta.bankQrUrl}
              alt="Club payment QR"
              className="h-[104px] w-[104px] flex-none border border-line object-contain"
            />
          ) : (
            <div className="grid h-[104px] w-[104px] flex-none place-items-center border border-dashed border-line-strong bg-ground text-center font-market text-[9.5px] leading-tight text-copy-label">
              No QR
              <br />
              yet
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="mb-1.5"><Pill tone="sent">QR / bank transfer</Pill></div>
            {meta.bankUpi && (
              <p className="font-market text-[15px] font-bold tracking-[-0.01em] text-ink-700">
                {meta.bankUpi}
              </p>
            )}
            {(meta.bankHolder || meta.bankAccount || meta.bankIfsc) && (
              <p className="mt-1 font-market text-[12.5px] leading-[1.7] tabular-nums text-copy">
                {meta.bankHolder && <>{meta.bankHolder}<br /></>}
                {meta.bankAccount && <>A/C {meta.bankAccount}</>}
                {meta.bankAccount && meta.bankIfsc && ' · '}
                {meta.bankIfsc && <>IFSC {meta.bankIfsc}</>}
              </p>
            )}
            {meta.bankDetails && (
              <p className="mt-1 whitespace-pre-line text-[12px] leading-relaxed text-copy-muted">
                {meta.bankDetails}
              </p>
            )}
            {!meta.bankUpi && !meta.bankAccount && !meta.bankQrUrl && (
              <p className="text-[12.5px] leading-relaxed text-copy-muted">
                Members opening a payment link are told what they owe but not
                where to send it. Add the club&rsquo;s UPI id, account number and
                IFSC, or a QR — and members can attach their payment screenshot
                as proof.
              </p>
            )}
            <p className="mt-2 font-market text-[10px] uppercase tracking-[0.14em] text-copy-label">
              Printed on every payment and joining link · members attach a
              payment screenshot
            </p>
          </div>
        </div>
      )}

      <GroundsBlock />
    </>
  );
}

/* ------------------------------------------------------------- grounds -- */

/**
 * The club's home grounds — the list every form offers.
 *
 * Adding one is a club-wide, effectively permanent decision (a membership
 * stores the key, not the label), which is why it belongs here and not inside
 * the add-member form. Removing is allowed, but only while nobody runs there:
 * the API refuses otherwise, and that refusal is the message shown.
 */
function GroundsBlock() {
  const { addGround, removeGround } = useClub();
  const { openSheet, toast } = useUI();
  const grounds = useGrounds();

  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const keys = Object.keys(grounds);

  async function add() {
    const label = name.trim();
    if (!label) return;
    setBusy(true);
    setError(null);
    try {
      const created = await addGround(label);
      setName('');
      toast(`${created.name} added`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that ground.');
    }
    setBusy(false);
  }

  async function remove(id, label) {
    setBusy(true);
    setError(null);
    try {
      await removeGround(id);
      toast(`${label} removed`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that ground.');
    }
    setBusy(false);
  }

  return (
    <div className="mt-8">
      <div className={SEC_HEAD}>
        <h2 className={SEC_TITLE}>Home grounds</h2>
      </div>

      {keys.length === 0 ? (
        <Empty title="No grounds yet">
          Add the places your club runs at — they are what every member form
          offers.
        </Empty>
      ) : (
        <div className="grid gap-px border border-line bg-line">
          {keys.map((id) => (
            <div
              key={id}
              className="flex flex-wrap items-center justify-between gap-2 bg-white px-4 py-3"
            >
              <span className="font-market text-[14px] font-bold text-ink-700">{grounds[id]}</span>
              <Button
                small
                tone="g"
                disabled={busy}
                onClick={() =>
                  openSheet(
                    <ConfirmSheet
                      title={`Remove ${grounds[id]}?`}
                      body="It disappears from every member form. Members who run there keep it until you move them — the club refuses the removal while anyone still does."
                      confirmLabel="Remove ground"
                      onConfirm={() => remove(id, grounds[id])}
                    />,
                  )
                }
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-[220px] flex-1">
          <Field label="Add a ground">
            <input
              value={name}
              placeholder="e.g. VOC Park"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void add();
                }
              }}
            />
          </Field>
        </div>
        <div className="mb-4">
          <Button tone="p" disabled={busy || !name.trim()} onClick={() => void add()}>
            {busy ? 'Saving…' : 'Add ground'}
          </Button>
        </div>
      </div>

      {error && <FormError>{error}</FormError>}

      <Hint>
        Members store the ground they run at, so one that is in use cannot be
        removed until those members are moved.
      </Hint>
    </div>
  );
}
